/**
 * Basic Information Section for Assistant Profile
 * Handles name, bio, and profile photo
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "./ImageUpload";

export default function BasicInfoSection({
  name,
  setName,
  bio,
  setBio,
  phone,
  setPhone,
  profileImage,
  setProfileImage,
  token,
  onImageUploaded
}) {
  // Handle image change and trigger immediate save
  const handleImageChange = (url) => {
    setProfileImage(url);
    // Trigger immediate save after state update (use setTimeout to ensure state is updated)
    // Save both when image is uploaded AND when removed
    if (onImageUploaded) {
      setTimeout(() => {
        onImageUploaded();
      }, 100);
    }
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
      
      <div className="space-y-6">
        {/* Profile Image */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider mb-4 block">Profile Photo</Label>
          <ImageUpload
            currentImage={profileImage}
            onImageChange={handleImageChange}
            token={token}
            label="Upload Photo"
            shape="circle"
            size="lg"
            fallbackText={name ? name.charAt(0).toUpperCase() : "?"}
          />
        </div>

        {/* Name */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Full Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
            required
            data-testid="name-input"
          />
        </div>

        {/* Phone */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Phone Number</Label>
          <Input
            type="tel"
            value={phone || ""}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
            data-testid="phone-input"
          />
          <p className="text-white/40 text-sm mt-1">Shared with studios/candidates only after a hire is confirmed.</p>
        </div>

        {/* Bio */}
        <div>
          <Label className="text-sm text-white/60 uppercase tracking-wider">Bio (2-3 paragraphs)</Label>
          <Textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell studios about your approach, reliability, and what makes you great to work with..."
            className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white min-h-[150px] rounded-sm"
            data-testid="bio-input"
          />
          <p className="text-white/40 text-sm mt-2">{bio.length}/1000 characters</p>
        </div>
      </div>
    </Card>
  );
}
